<template>
  <a-col v-if="isCompleted" class="form-qualities">
    <div class="quality-set__header">
      <div class="quality-set__title">Целевые качества университета</div>
      <div v-if="errors.getError('qualityIdList')" class="quality-set__error">
        {{ errors.getError('qualityIdList') }}
      </div>
    </div>

    <div class="quality-set__items">
      <template
        v-for="(mainQuality, index) in mainQualities"
        :key="'main-quality-' + index"
      >
        <c-checkbox
          :checked="getIsChecked(mainQuality.id)"
          :disabled="disabled"
          @change="handleQualityChange(mainQuality.id)"
        >
          {{ capitalizeFirstLetter(mainQuality.name) }}
        </c-checkbox>
      </template>
    </div>

    <div v-if="portfolioQualities.length > 0">
      <a-divider class="divider" />

      <div class="quality-set__header">
        <div class="quality-set__title">Целевые качества портфеля</div>
      </div>

      <div class="quality-set__items">
        <template
          v-for="(portfolioQuality, index) in portfolioQualities"
          :key="'portfolio-quality-' + index"
        >
          <c-checkbox
            :checked="getIsChecked(portfolioQuality.id)"
            :disabled="disabled"
            @change="handleQualityChange(portfolioQuality.id)"
          >
            {{ capitalizeFirstLetter(portfolioQuality.name) }}
          </c-checkbox>
        </template>
      </div>
    </div>
  </a-col>
</template>

<script setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import { useApi } from '@/api/use-api';
  import CCheckbox from '@/components/UI/CCheckbox.vue';
  import ErrorsHandler from '@/components/form/errors-handler';

  const props = defineProps({
    checkpoint: Object,
    isCompleted: Boolean,
    disabled: {
      type: Boolean,
      default: true,
    },
  });

  const api = useApi();
  const emit = defineEmits(['update', 'saved']);
  const projectQualityList = ref([]);
  const qualityIdList = ref([]);

  const errors = new ErrorsHandler({
    qualityIdList: '',
  });

  const mainQualities = computed(() => {
    return projectQualityList.value.filter((el) => el.isMain === true);
  });

  const portfolioQualities = computed(() => {
    return projectQualityList.value.filter((el) => el.isMain !== true);
  });

  onMounted(async () => {
    await getProjectQualityList();
    await getQualityIdList();
  });

  watch(
    () => props.isCompleted,
    () => {
      errors.clearErrors('qualityIdList');
    }
  );

  const getProjectQualityList = async () => {
    try {
      const response = await api.quality.index(
        new URLSearchParams({
          projectId: props.checkpoint.projectId,
        })
      );
      projectQualityList.value = response.qualities.items;
    } catch (error) {
      console.error('error', error);
    }
  };

  const getQualityIdList = async () => {
    try {
      const response = await api.checkpointQuality.performerQualities(
        new URLSearchParams({
          checkpointId: props.checkpoint.id,
        })
      );
      qualityIdList.value = response.qualities.items
        .filter((el) => el.isConfirmed)
        .map((el) => el.qualityId);
    } catch (error) {
      console.error('error', error);
    }
  };

  const getIsChecked = (qualityId) => {
    return qualityIdList.value.includes(qualityId);
  };

  const handleQualityChange = (qualityId) => {
    if (qualityIdList.value.includes(qualityId)) {
      qualityIdList.value = qualityIdList.value.filter(
        (el) => el !== qualityId
      );
    } else {
      qualityIdList.value.push(qualityId);
    }

    errors.clearErrors('qualityIdList');
    emit('update');
  };

  const onFinish = async () => {
    try {
      await api.checkpointQuality.savePerformerQualities({
        checkpointId: props.checkpoint.id,
        qualityIdList: qualityIdList.value,
      });

      emit('saved', true);
    } catch (error) {
      errors.handleApiErrors(error);
      emit('saved', false);
    }
  };
  defineExpose({ onFinish });

  function capitalizeFirstLetter(str) {
    return str[0].toUpperCase() + str.slice(1);
  }
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  .form-qualities {
    background: $color-bg-secondary-grey;
    border-radius: 6px;
    flex-direction: column;
    margin-top: 30px;
    padding: 20px;
    height: 100%;

    .quality-set {
      &__header {
        margin-bottom: 20px;
      }

      &__title {
        color: $color-bg-status-new;
        font: 700 14px/20px 'Nunito Sans';
      }

      &__items {
        display: flex;
        flex-direction: column;
        gap: 14px 0;
      }

      &__error {
        color: $color-text-accent-red;
        margin-bottom: 14px;
      }
    }

    .divider {
      border-color: $color-bg-primary-white;
    }
  }
</style>
